import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";

const ServiceTypeNewEdit = () => {
  usePageTitle("Edit Service Type");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();

  const entityType = overlayData?.entityType === "serviceSubtype" ? "serviceSubtype" : "clientServiceType";
  const isServiceSubtypeMode = entityType === "serviceSubtype";
  const recordId = isServiceSubtypeMode
    ? overlayData?.serviceSubtypeId
    : overlayData?.serviceTypeId;
  const isEditMode = !!recordId;
  const allServiceTypes = useMemo(
    () => overlayData?.allServiceTypes || [],
    [overlayData?.allServiceTypes],
  );
  const usageCount = Number(overlayData?.usageCount || 0);

  const [name, setName] = useState(overlayData?.name || "");
  const [initialName] = useState(overlayData?.name || "");
  const [serviceCategory, setServiceCategory] = useState(
    overlayData?.serviceCategory || "Interpretation",
  );
  const [isActive, setIsActive] = useState(
    overlayData?.isActive === undefined ? true : Boolean(overlayData?.isActive),
  );
  const [sortOrder, setSortOrder] = useState(Number(overlayData?.sortOrder || 0));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const isDuplicateName = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;

    return allServiceTypes.some(
      (serviceType) =>
        (serviceType.Name || "").toLowerCase() === trimmed &&
        (!isServiceSubtypeMode ||
          String(serviceType.ServiceCategory || "") === String(serviceCategory || "")) &&
        serviceType.id !== recordId,
    );
  }, [name, serviceCategory, allServiceTypes, isServiceSubtypeMode, recordId]);

  const emitSettingsSaved = (recordId, changeAction) => {
    if (!socket || !currentUser?.id) return;

    socket.emit("settings:saved", {
      settingsType: isServiceSubtypeMode ? "ServiceSubtype" : "ServiceType",
      recordId,
      changeAction,
      user: {
        id: currentUser.id,
        name: currentUser.Name,
        imageThumbnail: currentUser.ImageThumbnail,
      },
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar(
        isServiceSubtypeMode
          ? "Service type name is required"
          : "Client service type name is required",
        theme,
        "error",
      );
      return;
    }

    if (isDuplicateName) {
      showSnackbar(
        isServiceSubtypeMode
          ? "This service type already exists"
          : "This client service type already exists",
        theme,
        "error",
      );
      return;
    }

    const hasChanges = !isEditMode
      ? true
      : isServiceSubtypeMode
      ? name.trim() !== initialName.trim() ||
        String(serviceCategory || "") !== String(overlayData?.serviceCategory || "") ||
        Boolean(isActive) !== Boolean(overlayData?.isActive) ||
        Number(sortOrder || 0) !== Number(overlayData?.sortOrder || 0)
      : name.trim() !== initialName.trim();

    if (!hasChanges) {
      showSnackbar("No changes to save", theme, "info");
      return;
    }

    setSaving(true);

    try {
      if (isServiceSubtypeMode) {
        if (isEditMode) {
          await api.put(`/services/settings/subtypes/${recordId}`, {
            Name: name.trim(),
            ServiceCategory: serviceCategory,
            IsActive: Boolean(isActive),
            SortOrder: Number(sortOrder) || 0,
          });
          showSnackbar("Service type updated successfully", theme, "success");
          emitSettingsSaved(recordId, "Updated");
        } else {
          const response = await api.post("/services/settings/subtypes", {
            Name: name.trim(),
            ServiceCategory: serviceCategory,
            IsActive: Boolean(isActive),
            SortOrder: Number(sortOrder) || 0,
          });
          showSnackbar("Service type created successfully", theme, "success");
          emitSettingsSaved(response.data?.id, "Created");
        }
      } else {
        if (isEditMode) {
          await api.put(`/clients/settings/serviceTypes/${recordId}`, {
            Name: name.trim(),
          });
          showSnackbar("Client service type updated successfully", theme, "success");
          emitSettingsSaved(recordId, "Updated");
        } else {
          const response = await api.post("/clients/settings/serviceTypes", {
            Name: name.trim(),
          });
          showSnackbar("Client service type created successfully", theme, "success");
          emitSettingsSaved(response.data?.id, "Created");
        }
      }

      overlayData?.onSaved?.();
      closeOverlay();
    } catch (err) {
      console.error(err);
      handleErrorDialog(
        err?.response?.data?.error ||
          (isServiceSubtypeMode
            ? "Error saving service type"
            : "Error saving client service type"),
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!isEditMode) return;

    if (usageCount > 0) {
      showSnackbar(
        isServiceSubtypeMode
          ? `Cannot delete. In use by ${usageCount} service${usageCount > 1 ? "s" : ""}`
          : `Cannot delete. In use by ${usageCount} client${usageCount > 1 ? "s" : ""}`,
        theme,
        "error",
      );
      return;
    }

    openDialog({
      theme,
      title: isServiceSubtypeMode ? "Delete Service Type" : "Delete Client Service Type",
      message: `Are you sure you want to delete "${name}"?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        setDeleting(true);
        try {
          if (isServiceSubtypeMode) {
            await api.delete(`/services/settings/subtypes/${recordId}`);
          } else {
            await api.delete(`/clients/settings/serviceTypes/${recordId}`);
          }
          closeDialog();
          showSnackbar(
            isServiceSubtypeMode ? "Service type deleted" : "Client service type deleted",
            theme,
            "success",
          );
          emitSettingsSaved(recordId, "Deleted");
          overlayData?.onSaved?.();
          closeOverlay();
        } catch (err) {
          console.error(err);
          closeDialog();
          handleErrorDialog(
            err?.response?.data?.error ||
              (isServiceSubtypeMode
                ? "Error deleting service type"
                : "Error deleting client service type"),
            theme,
          );
        } finally {
          setDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const pageTitle = isServiceSubtypeMode
    ? isEditMode
      ? "Edit Service Type"
      : "New Service Type"
    : isEditMode
    ? "Edit Client Service Type"
    : "New Client Service Type";

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "800px",
          maxHeight: "80%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: { xs: "0.5rem 0.5rem 0.75rem 0.5rem", sm: "0rem .2rem 1.5rem 0" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: { xs: "0.5rem 0.75rem 0.5rem 0.75rem", sm: "0.5rem 2rem 0.5rem 2rem" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {pageTitle}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isEditMode && (
              isMobile ? (
                <IconButton
                  color="error"
                  onClick={handleDelete}
                  disabled={deleting || usageCount > 0}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.error.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DeleteRoundedIcon />
                </IconButton>
              ) : (
                <Button
                  loading={deleting}
                  color="error"
                  variant="outlined"
                  size="large"
                  endIcon={<DeleteRoundedIcon />}
                  onClick={handleDelete}
                  disabled={deleting || usageCount > 0}
                  sx={{ borderRadius: "12px" }}
                >
                  Delete
                </Button>
              )
            )}

            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={closeOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={closeOverlay}
                sx={{ borderRadius: "12px" }}
              >
                Discard
              </Button>
            )}

            <Tooltip
              title={isMobile && (saving || isDuplicateName) ? "Please fix errors before saving" : ""}
              placement="top"
            >
              <span>
                {isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || isDuplicateName}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                ) : (
                  <Button
                    loading={saving}
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={saving || isDuplicateName}
                    sx={{ borderRadius: "12px" }}
                  >
                    {isEditMode ? "Update" : "Create"}
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: { xs: "1rem 1rem 1rem 1rem", sm: "1rem 2.3rem 1rem 2.5rem" },
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          {isEditMode && usageCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {isServiceSubtypeMode
                ? `This service type is used by ${usageCount} service${usageCount > 1 ? "s" : ""} and cannot be deleted.`
                : `This client service type is used by ${usageCount} client${usageCount > 1 ? "s" : ""} and cannot be deleted.`}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.25,
            }}
          >
            {isServiceSubtypeMode && (
              <TextField
                fullWidth
                select
                size="small"
                label="Category"
                value={serviceCategory}
                onChange={(event) => setServiceCategory(event.target.value)}
              >
                <MenuItem value="Interpretation">Interpretation</MenuItem>
                <MenuItem value="Translation">Translation</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            )}

            <TextField
              fullWidth
              size="small"
              label={isServiceSubtypeMode ? "Service Type Name" : "Client Service Type Name"}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={
                isServiceSubtypeMode
                  ? "e.g., On-Site Interpretation"
                  : "e.g., Legal Services"
              }
              error={isDuplicateName}
              helperText={isDuplicateName ? "This name already exists" : ""}
            />

            {isServiceSubtypeMode && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Sort Order"
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
              />
            )}

            {isServiceSubtypeMode && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pt: 0.25,
                }}
              >
                <Switch
                  checked={Boolean(isActive)}
                  onChange={(event) => setIsActive(event.target.checked)}
                />
                <Typography variant="body2">
                  {isActive ? "Active" : "Archived"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceTypeNewEdit;
